import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * UserBar - User authentication and profile bar in header
 *
 * Shows:
 * - If not logged in: Sign up and Login links
 * - If logged in:
 *   - User profile picture and name with dropdown menu
 *   - Alerts/notifications bell
 *   - Dropdown menu with: View Profile, Users Management (admin), Projects Management, Logout
 */
function UserBar() {
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [alertsCount] = useState(0);
  const [alerts] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const alertsRef = useRef(null);
  const alertsBellRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      // Check both the alerts panel and the bell button
      const clickedOnBell = alertsBellRef.current && alertsBellRef.current.contains(event.target);
      const clickedOnPanel = alertsRef.current && alertsRef.current.contains(event.target);
      if (!clickedOnBell && !clickedOnPanel) {
        setAlertsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e) => {
    e.preventDefault();
    setDropdownOpen(!dropdownOpen);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    logout();
  };

  const handleProjectManagement = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    navigate('/projectmanagement');
  };

  const handleUserManagement = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    navigate('/usersmanagement');
  };

  const handleQueryManagement = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    navigate('/querymanagement');
  };

  const handleMcpHelp = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    navigate('/mcp-help');
  };

  const handleViewProfile = () => {
    setDropdownOpen(false);
  };

  const toggleAlerts = (e) => {
    e.preventDefault();
    setAlertsOpen(!alertsOpen);
  };

  const closeAlerts = (e) => {
    e.preventDefault();
    setAlertsOpen(false);
  };

  // Get user initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (!isLoggedIn) {
    return (
      <ul className="user-header-list nav navbar-nav signup">
        <li>
          <Link to="/signup" className="internal-link">
            <i className="glyphicon glyphicon-edit"></i> Sign up
          </Link>
        </li>
        <li>
          <Link to="/login" className="internal-link">
            <i className="glyphicon glyphicon-log-in"></i> Login
          </Link>
        </li>
      </ul>
    );
  }

  const isCompanyUser = user?.isCompanyUser || false;
  const shouldShowProjectManagement = isAdmin || isCompanyUser;

  return (
    <>
      <ul className="user-header-list nav navbar-nav">
        {/* User profile dropdown */}
        <li
          ref={dropdownRef}
          className={`user-header-user dropdown navbar-left ${dropdownOpen ? 'open' : ''}`}
          role="presentation"
        >
          <a
            type="button"
            className="user-header-user-btn dropdown-toggle"
            onClick={toggleDropdown}
            aria-expanded={dropdownOpen}
            style={{ cursor: 'pointer' }}
          >
            {user?.profilePic ? (
              <img
                alt="User profile"
                className="img-rounded user-header-pic-display"
                src={user.profilePic}
              />
            ) : (
              <span
                className="user-header-pic-display user-initials-avatar"
                title={user?.name || user?.username}
              >
                {getInitials(user?.name || user?.username)}
              </span>
            )}
            {user?.name || user?.username} ({user?.username}) <span className="caret"></span>
          </a>
          <ul className="dropdown-menu" role="menu" style={{ display: dropdownOpen ? 'block' : 'none' }}>
            <li role="presentation">
              <Link
                className="user-header-username-view-profile-link"
                role="menuitem"
                tabIndex="-1"
                to={`/user/${user?.username}`}
                onClick={handleViewProfile}
              >
                <i className="glyphicon glyphicon-user fa-lg"></i> View Profile
              </Link>
            </li>

            {/* Users Management - admin only, not company users */}
            {isAdmin && !isCompanyUser && (
              <li role="presentation">
                <a
                  role="menuitem"
                  tabIndex="-1"
                  className="user-management-btn"
                  href="#"
                  onClick={handleUserManagement}
                >
                  <i className="fa fa-users fa-lg"></i>&nbsp;Users Management
                </a>
              </li>
            )}

            {/* Projects Management - admin or company users */}
            {shouldShowProjectManagement && (
              <li role="presentation">
                <a
                  role="menuitem"
                  tabIndex="-1"
                  className="project-management-btn"
                  href="#"
                  onClick={handleProjectManagement}
                >
                  <i className="glyphicon glyphicon-road fa-lg"></i>&nbsp;Projects Management
                </a>
              </li>
            )}

            {/* Query Management - admin only (not company users) */}
            {isAdmin && !isCompanyUser && (
              <li role="presentation">
                <a
                  role="menuitem"
                  tabIndex="-1"
                  className="query-management-btn"
                  href="#"
                  onClick={handleQueryManagement}
                >
                  <i className="glyphicon glyphicon-list fa-lg"></i>&nbsp;Query Management
                </a>
              </li>
            )}

            <li role="presentation">
              <a
                role="menuitem"
                tabIndex="-1"
                className="mcp-help-btn"
                href="#"
                onClick={handleMcpHelp}
              >
                <i className="fa-solid fa-robot fa-lg"></i>&nbsp;MCP Help
              </a>
            </li>

            <li role="presentation">
              <a role="menuitem" className="logout-link" tabIndex="-1" href="/logout" onClick={handleLogout}>
                <i className="glyphicon glyphicon-log-out fa-lg"></i> Log Out
              </a>
            </li>
          </ul>
        </li>

        {/* Alert count badge */}
        {alertsCount > 0 && (
          <li className="user-header-alert-count navbar-right">{alertsCount}</li>
        )}

        {/* Alerts bell */}
        <li
          ref={alertsBellRef}
          className={`user-header-alert navbar-right ${alertsCount > 0 ? 'new-alert' : ''}`}
          role="presentation"
        >
          <a
            type="button"
            className={`user-header-user-btn dropdown-toggle userbar-alert-btn ${alertsCount > 0 ? 'new-alert' : ''}`}
            onClick={toggleAlerts}
            aria-expanded={alertsOpen}
            style={{ padding: '8px', cursor: 'pointer' }}
          >
            <i className="glyphicon glyphicon-bell fa-lg"></i>
          </a>
        </li>
      </ul>

      {/* Alerts dropdown panel - positioned outside ul like original Backbone */}
      <div
        ref={alertsRef}
        className="user-alerts-placeholder"
        style={{ display: alertsOpen ? 'block' : 'none' }}
      >
        <div className="user-alerts-menu text-center">
          <p>
            Notifications
            <a
              className="user-alert-container-close pull-right"
              onClick={closeAlerts}
              style={{ cursor: 'pointer' }}
            >
              <i className="glyphicon glyphicon-remove"></i>
            </a>
          </p>
        </div>
        <div className="user-alerts-container">
          <ul className="user-alerts-list">
            {alerts.length === 0 ? (
              <li role="presentation" className="user-bar-view-no-alert">
                <div className="userbar-alert-li">
                  <p>No alerts.</p>
                </div>
              </li>
            ) : (
              alerts.map((alert, index) => (
                <li key={alert.alertId || index} role="presentation">
                  <div className="userbar-alert-li">
                    <p>{alert.message || alert.content}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

    </>
  );
}

export default UserBar;
